// ChangePassword — the one thing the classic /profile screen did that a
// crew member still needs: change their own password. A Sheet with three
// fields; PUT /api/auth/password { currentPassword, newPassword } — the
// server checks the current one (401 with a sentence when wrong) and the
// length (8+). Used by Field → Me and Dispatch → Me.

import React, { useState } from 'react';
import { Sheet, FwButton, FwField, FwInput, Notice } from '../../ui/fw';
import { put, describeError } from './http';

export const MIN_LENGTH = 8;

/** What is wrong with the form, or null when it can be sent. */
export function passwordProblem({ current, next, again }) {
  if (!current) return 'Type your current password.';
  if (!next || next.length < MIN_LENGTH) return `The new password needs at least ${MIN_LENGTH} characters.`;
  if (next === current) return 'The new password is the same as the current one.';
  if (next !== again) return 'The two copies of the new password do not match.';
  return null;
}

export default function ChangePassword({ open, onClose, send }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const problem = passwordProblem({ current, next, again });
  const touched = current || next || again;

  const reset = () => { setCurrent(''); setNext(''); setAgain(''); setError(null); setDone(false); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (problem) { setError(problem); return; }
    setBusy(true);
    setError(null);
    try {
      const doSend = send || ((body) => put('/auth/password', body));
      await doSend({ currentPassword: current, newPassword: next });
      setDone(true);
      setCurrent(''); setNext(''); setAgain('');
    } catch (err) {
      // 401 here means "current password is wrong", not "session expired" —
      // the one place describeError's 401 sentence would mislead.
      if (err && err.status === 401) setError((err.body && err.body.error) || 'Current password is incorrect.');
      else setError(describeError(err, 'The password was not changed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      title="Change password"
      onClose={close}
      footer={done
        ? <FwButton size="lg" full onClick={close}>Done</FwButton>
        : <FwButton size="lg" full type="submit" form="fw-change-password" loading={busy} disabled={!touched}>Change password</FwButton>}
    >
      {done ? (
        <Notice tone="ok" title="Password changed">Use the new one next time you sign in.</Notice>
      ) : (
        <form id="fw-change-password" className="fw-form" onSubmit={submit} noValidate>
          {error ? <Notice tone="stop" onDismiss={() => setError(null)}>{error}</Notice> : null}
          <FwField label="Current password" htmlFor="fw-pw-current" required>
            <FwInput id="fw-pw-current" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          </FwField>
          <FwField label="New password" htmlFor="fw-pw-next" hint={`At least ${MIN_LENGTH} characters.`} required>
            <FwInput id="fw-pw-next" type="password" autoComplete="new-password" minLength={MIN_LENGTH} value={next} onChange={(e) => setNext(e.target.value)} />
          </FwField>
          <FwField label="New password again" htmlFor="fw-pw-again" required error={touched && again && next !== again ? 'Does not match.' : null}>
            <FwInput id="fw-pw-again" type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
          </FwField>
        </form>
      )}
    </Sheet>
  );
}
